import React, { useState, useEffect } from 'react';
import api from '../../../services/api';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_REGEX = /^(\+977-?)?[0-9]{10}$/;
const VAT_REGEX = /^[0-9]{9}$/;

const EMPTY_FORM = {
  name: '',
  email: '',
  password: '',
  primaryPhone: '',
  city: '',
  vatNumber: '',
  companyRegistrationCertificate: null
};

// Modal for creating a vendor inline
export default function VendorModal({ open, onClose, onCreate, error, setError, creating }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [fieldErrors, setFieldErrors] = useState({});
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(EMPTY_FORM);
      setFieldErrors({});
      setError && setError('');
    }
  }, [open, setError]);

  if (!open) return null;

  const updateField = (field, value) => {
    setForm((f) => ({ ...f, [field]: value }));
    setFieldErrors((prev) => (prev[field] ? { ...prev, [field]: undefined } : prev));
    if (error) setError('');
  };

  const validate = () => {
    const errors = {};

    if (!form.name.trim()) errors.name = 'Vendor name is required';
    if (!form.email.trim()) errors.email = 'Email is required';
    else if (!EMAIL_REGEX.test(form.email.trim())) errors.email = 'Please enter a valid email address';
    if (!form.password) errors.password = 'Password is required';
    else if (form.password.length < 6) errors.password = 'Password must be at least 6 characters long';
    if (!form.primaryPhone.trim()) errors.primaryPhone = 'Primary phone is required';
    else if (!PHONE_REGEX.test(form.primaryPhone.replace(/[\s-]/g, ''))) errors.primaryPhone = 'Please enter a valid phone number';
    if (!form.city.trim()) errors.city = 'City is required';
    if (!form.vatNumber.trim()) errors.vatNumber = 'VAT number is required';
    else if (!VAT_REGEX.test(form.vatNumber.trim())) errors.vatNumber = 'VAT number must be exactly 9 digits';
    if (!form.companyRegistrationCertificate) errors.companyRegistrationCertificate = 'Company registration certificate is required';

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    let certificateUrl = form.companyRegistrationCertificate;
    if (certificateUrl instanceof File) {
      try {
        setUploading(true);
        const fileFormData = new FormData();
        fileFormData.append('files', certificateUrl);
        const mediaResponse = await api.post('/media/upload', fileFormData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        if (mediaResponse.data?.success && mediaResponse.data?.data?.[0]?.url) {
          certificateUrl = mediaResponse.data.data[0].url;
        } else {
          throw new Error('Upload did not return a file URL');
        }
      } catch (err) {
        setError && setError('Failed to upload company registration certificate. Please try again.');
        setUploading(false);
        return;
      }
      setUploading(false);
    }

    onCreate({
      name: form.name.trim(),
      email: form.email.trim(),
      password: form.password,
      primaryPhone: form.primaryPhone.trim(),
      city: form.city.trim(),
      vatNumber: form.vatNumber.trim(),
      companyRegistrationCertificate: certificateUrl
    });
  };

  const isBusy = creating || uploading;

  const fieldClass = (field) =>
    `mt-1 w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-admin-slate-700 dark:text-white ${
      fieldErrors[field] ? 'border-red-400 dark:border-red-500' : 'border-gray-300 dark:border-admin-slate-600'
    }`;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-lg bg-white dark:bg-admin-slate-800 rounded-lg shadow-xl border border-gray-200 dark:border-admin-slate-700 flex flex-col max-h-[90vh]">
        <div className="px-5 py-4 border-b border-gray-200 dark:border-admin-slate-700 flex items-center justify-between flex-shrink-0">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Create Vendor</h3>
          <button type="button" className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300" onClick={onClose}>×</button>
        </div>
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Vendor Name <span className="text-red-500">*</span></label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => updateField('name', e.target.value)}
                className={fieldClass('name')}
              />
              {fieldErrors.name && <p className="mt-1 text-xs text-red-600">{fieldErrors.name}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Email Address <span className="text-red-500">*</span></label>
              <input
                type="email"
                value={form.email}
                onChange={(e) => updateField('email', e.target.value)}
                className={fieldClass('email')}
              />
              {fieldErrors.email && <p className="mt-1 text-xs text-red-600">{fieldErrors.email}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Password <span className="text-red-500">*</span></label>
              <input
                type="password"
                value={form.password}
                onChange={(e) => updateField('password', e.target.value)}
                className={fieldClass('password')}
              />
              {fieldErrors.password && <p className="mt-1 text-xs text-red-600">{fieldErrors.password}</p>}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Primary Phone <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  value={form.primaryPhone}
                  onChange={(e) => updateField('primaryPhone', e.target.value)}
                  placeholder="+977-XXXXXXXXXX"
                  className={fieldClass('primaryPhone')}
                />
                {fieldErrors.primaryPhone && <p className="mt-1 text-xs text-red-600">{fieldErrors.primaryPhone}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">City <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  value={form.city}
                  onChange={(e) => updateField('city', e.target.value)}
                  placeholder="Enter city"
                  className={fieldClass('city')}
                />
                {fieldErrors.city && <p className="mt-1 text-xs text-red-600">{fieldErrors.city}</p>}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">VAT Number <span className="text-red-500">*</span></label>
              <input
                type="text"
                value={form.vatNumber}
                onChange={(e) => updateField('vatNumber', e.target.value)}
                placeholder="9-digit VAT number"
                className={fieldClass('vatNumber')}
              />
              {fieldErrors.vatNumber && <p className="mt-1 text-xs text-red-600">{fieldErrors.vatNumber}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Company Registration Certificate <span className="text-red-500">*</span></label>
              <input
                type="file"
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={(e) => updateField('companyRegistrationCertificate', e.target.files?.[0] || null)}
                className={fieldClass('companyRegistrationCertificate') + ' file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:bg-primary-50 file:text-primary-700 dark:file:bg-admin-slate-600 dark:file:text-white'}
              />
              {fieldErrors.companyRegistrationCertificate && <p className="mt-1 text-xs text-red-600">{fieldErrors.companyRegistrationCertificate}</p>}
            </div>
          </div>
          {error && (
            <div className="rounded-md bg-red-50 dark:bg-red-900/20 p-3 border border-red-200 dark:border-red-800">
              <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
            </div>
          )}
        </div>
        <div className="px-5 py-4 border-t border-gray-200 dark:border-admin-slate-700 flex items-center justify-end gap-2 flex-shrink-0">
          <button type="button" onClick={onClose} className="px-4 py-2 bg-gray-100 dark:bg-admin-slate-700 text-gray-700 dark:text-gray-300 rounded-md">Cancel</button>
          <button
            type="button"
            disabled={isBusy}
            onClick={handleSubmit}
            className="px-4 py-2 bg-primary-600 text-white rounded-md disabled:opacity-50"
          >
            {uploading ? 'Uploading...' : creating ? 'Creating...' : 'Create'}
          </button>
        </div>
      </div>
    </div>
  );
}
